import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './App.css';
import { Monitor, Layout, Map, Bug, CreditCard, Settings, MonitorPlay, Database } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import LoginButton from './LoginButton';
import LogoutButton from './LogoutButton';
import TemplateList from './components/TemplateList';
import TemplateEditor from './components/TemplateEditor';
import DeviceManager from './components/DeviceManager';
import FloorPlan from './components/FloorPlan';
import PairView from './components/PairView';
import DisplayView from './components/DisplayView';
import BillingPage from './components/BillingPage';
import SettingsPage from './components/SettingsPage';
import DataSources from './components/DataSources';   // eslint-disable-line no-unused-vars -- kept: flip 'data' back off `soon` to restore
import ComingSoon from './components/ComingSoon';
import Wizard from './components/Wizard';
import { setApiTokenProvider } from './api';

const API_BASE_URL = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
const SOCKET_URL = API_BASE_URL || undefined;

const DEV_USER = { sub: 'dev|local', name: 'Dev User', email: 'dev@localhost' };

// PUTTV cast mark — same glyph as the landing site favicon, recolored for the
// ops-console theme: signal-amber tile, ink signal waves.
export const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
    <rect width="100" height="100" rx="20" fill="#f0a11c" />
    <path d="M30 38a26 26 0 0 1 40 0" stroke="#16181d" strokeWidth="7" fill="none" strokeLinecap="round" />
    <path d="M40 50a13 13 0 0 1 20 0" stroke="#16181d" strokeWidth="7" fill="none" strokeLinecap="round" />
    <circle cx="50" cy="66" r="6" fill="#16181d" />
  </svg>
);

export const Brand = ({ size = 28 }) => (
  <div className="brand">
    <Logo size={size} />
    <span className="brand-name">PUTTV</span>
  </div>
);

function App({ devMode = false }) {
  // In devMode (no Auth0 creds configured) we mock the auth state so every
  // part of the UI is reachable without signing in.
  const auth0 = devMode
    ? { isAuthenticated: true, isLoading: false, error: null, user: DEV_USER }
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useAuth0(); // only called when Auth0Provider is in the tree

  const { isAuthenticated, isLoading, error, user, getAccessTokenSilently } = auth0;

  // Give the REST client a way to mint access tokens so every API call carries
  // Authorization: Bearer (the server verifies it when Auth0 is configured).
  // Assigned during render, NOT in an effect: children (TemplateList etc.) fire
  // their data-fetch effects before a parent effect would run, and their first
  // request must already carry the token. Idempotent, so safe on re-renders.
  if (!devMode && isAuthenticated && getAccessTokenSilently) {
    setApiTokenProvider(() => getAccessTokenSilently());
  } else {
    setApiTokenProvider(null);
  }

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [message, setMessage] = useState('');
  const [debugMessages, setDebugMessages] = useState([]);
  const [debugOpen, setDebugOpen] = useState(false);

  const [tab, setTab] = useState('screens');
  const [editing, setEditing] = useState(undefined); // undefined = list, null = new, object = edit

  // Pairing deep-link: /pair?token=... or a stashed token after Auth0 round-trip
  const [showPair, setShowPair] = useState(
    window.location.pathname === '/pair' || !!localStorage.getItem('pendingPairToken')
  );

  // /display — the web-kiosk entry point smart TVs load directly. Fully
  // public (no Auth0 login on a TV), self-provisions its own device identity
  // and pairs via QR, same as PairView bypasses the dashboard below.
  // Also triggered by hostname alone (tv.puttv.io's root path) so the URL a
  // TV remote has to type is as short as possible — no path to enter.
  const showDisplay =
    window.location.pathname === '/display' ||
    window.location.hostname.split('.')[0] === 'tv';

  useEffect(() => {
    // DisplayView/PairView open and manage their own sockets — the dashboard's
    // socket has no reason to exist on those screens (it was previously
    // connecting anyway, identifying as an anonymous frontend client, and
    // reconnecting forever in the background on every /display and /pair load).
    if (showDisplay || showPair) return;
    let newSocket;
    try {
      newSocket = io(SOCKET_URL || undefined, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 45000,
        autoConnect: true,
      });
    } catch (err) {
      newSocket = io(SOCKET_URL || 'http://localhost:3001');
    }

    newSocket.on('connect', () => {
      setConnected(true);
      const identifyData = { clientType: 'frontend' };
      if (isAuthenticated && user) {
        identifyData.userId = user.sub;
        identifyData.userEmail = user.email;
      }
      newSocket.emit('identify', identifyData);
    });

    newSocket.on('disconnect', () => { setConnected(false); setMessage('Disconnected from server'); });
    newSocket.on('connect_error', (e) => setMessage(`Connection error: ${e.message}`));
    newSocket.on('message', (m) => setMessage(m));
    newSocket.on('debug_message', (d) => setDebugMessages((prev) => [d, ...prev].slice(0, 50)));

    setSocket(newSocket);
    return () => newSocket.disconnect();
    // showPair/showDisplay so leaving PairView (exitPair -> setShowPair(false))
    // still connects the dashboard socket at that point, not just on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDisplay, showPair]);

  // Re-identify with user info once authenticated (also re-runs on reconnect).
  // Includes the Auth0 access token so the server can verify who this socket is.
  useEffect(() => {
    if (socket && connected && isAuthenticated && user) {
      (async () => {
        const identify = { clientType: 'frontend', userId: user.sub, userEmail: user.email };
        if (!devMode && getAccessTokenSilently) {
          try { identify.token = await getAccessTokenSilently(); } catch (_) { /* anonymous */ }
        }
        socket.emit('identify', identify);
      })();
    }
  }, [socket, connected, isAuthenticated, user, devMode, getAccessTokenSilently]);

  // Transient toast: any message auto-dismisses.
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const sendTvCommand = (command, params = {}) => {
    if (socket && connected) {
      const data = { command, params };
      if (isAuthenticated && user) data.userId = user.sub;
      socket.emit('tv_command', data);
      setMessage(`Sent TV command: ${command}`);
    } else {
      setMessage('Not connected to server');
    }
  };

  const exitPair = () => {
    localStorage.removeItem('pendingPairToken');
    window.history.replaceState({}, '', '/');
    setShowPair(false);
  };

  // ---- conditional rendering (all hooks above are always called) ----
  if (showDisplay) return <DisplayView />;
  if (showPair) return <PairView onDone={exitPair} />;

  if (isLoading) {
    return <div className="loading-screen">Loading…</div>;
  }

  if (error) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-brand"><Logo size={34} /><span className="brand-name">PUTTV</span></div>
          <p className="tagline">Something went wrong</p>
          <p className="error-text">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-brand">
            <Logo size={34} />
            <span className="brand-name">PUTTV</span>
          </div>
          <p className="tagline">Sign in to manage your screens.</p>
          <LoginButton />
          <p className="legal-note">
            By signing in or creating an account you agree to the{' '}
            <a href="https://puttv.io/terms" target="_blank" rel="noopener noreferrer">Customer Agreement</a>.
          </p>
        </div>
      </div>
    );
  }

  const userId = user?.sub;
  const NAV = [
    { key: 'screens', label: 'Screens', icon: Monitor },
    { key: 'templates', label: 'Templates', icon: Layout },
    { key: 'data', label: 'Data sources', icon: Database, soon: true },
    { key: 'floorplan', label: 'Floor plan', icon: Map },
    { key: 'wizard', label: 'See it work', icon: MonitorPlay },
    { key: 'billing', label: 'Billing', icon: CreditCard },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="shell">
      <aside className="sidebar">
        <Brand />

        <nav className="nav">
          {NAV.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} className={`nav-item${tab === t.key ? ' active' : ''}`}
                onClick={() => { setTab(t.key); setEditing(undefined); }}>
                <Icon size={18} /> <span>{t.label}</span>
                {t.soon && <span className="nav-soon">Soon</span>}
              </button>
            );
          })}
        </nav>

        <div className="side-foot">
          <span className="conn-pill">
            <span className={`status-dot ${connected ? 'on' : 'off'}`} />
            {connected ? 'Connected' : 'Offline'}
          </span>
          {debugMessages.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setDebugOpen((v) => !v)}>
              <Bug size={16} /> Debug ({debugMessages.length})
            </button>
          )}
          <div className="user-row">
            <span className="user-name">{user?.name}{devMode && ' (dev)'}</span>
            {!devMode && <LogoutButton />}
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="page">
          {tab === 'screens' && (
            <DeviceManager socket={socket} userId={userId} userEmail={user?.email}
              onPowerAll={sendTvCommand} />
          )}
          {tab === 'templates' && (
            editing === undefined
              ? <TemplateList userId={userId} onEdit={setEditing} />
              : <TemplateEditor template={editing} userId={userId}
                  onSaved={() => setEditing(undefined)} onCancel={() => setEditing(undefined)} />
          )}
          {tab === 'data' && (
            <ComingSoon
              icon={Database}
              title="Data sources"
              blurb="Pull live numbers from your own systems onto your screens."
              points={[
                'Point PUTTV at any API — prices, queue numbers, KPIs, stock levels, arrivals.',
                'The server polls on your schedule and pushes changes to every screen showing that feed.',
                'Bind the result to a Data Feed widget; the screen refreshes itself with no edits.',
              ]}
              note="We're holding this until credential storage is finished — connecting your systems means trusting us with your API tokens, and we want that airtight before you hand any over."
            />
          )}
          {tab === 'floorplan' && <FloorPlan socket={socket} userId={userId} />}
          {/* Mounted only while open: it provisions live demo screens, so it must
              not run in the background behind another tab. */}
          {tab === 'wizard' && (
            <Wizard userId={userId} userEmail={user?.email} onExit={() => setTab('screens')} />
          )}
          {tab === 'billing' && <BillingPage userId={userId} />}
          {tab === 'settings' && <SettingsPage userId={userId} userEmail={user?.email} />}

          {debugOpen && debugMessages.length > 0 && (
            <div className="debug-panel">
              {debugMessages.map((m, i) => (
                <div key={i} className="debug-line">
                  <span className="ts">{m.timestamp}</span>{m.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {message && <div className="toast">{message}</div>}
    </div>
  );
}

export default App;
